import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiGetSubGrupos } from "../api/subGruposApi";
import {
  apiDeletePreguntas,
  apiGetPreguntas,
  apiSavePreguntas,
} from "../api/RiskQuestionsApi";
import {
  apiGetHelpByPregunta,
  apiSaveHelp,
  apiDeleteHelp,
} from "../api/helpQuestionApi";

const emptyForm = {
  id: "",
  IdSubgrupos: "",
  Orden: "",
  Pregunta: "",
  MasInformacion: "",
  Soporte: "",
  Accion: "",
  idSubgrupo_Si: "",
  idSubgrupo_No: "",
  Dx: "",
  sexo: "A",
};

const emptyHelp = {
  MasInfo: "",
  orden: 1,
};

export const useRiskQuestions = () => {
  const [subgroups, setSubGroups] = useState([]);
  const [subgroupsFilter, setSubGroupsFilter] = useState([]);
  const [subgroupsSi, setSubGroupsSi] = useState([]);
  const [subgroupsNo, setSubGroupsNo] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPreguntaRiesgo, setSelectedPreguntaRiesgo] = useState(null);
  const [groupFilterSelected, setGroupFilterSelected] = useState("");
  const [groupSelected, setGroupSelected] = useState("");
  const [groupSiSelected, setGroupSiSelected] = useState("");
  const [groupNoSelected, setGroupNoSelected] = useState("");
  const [preguntaRiesgo, setPreguntaRiesgo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Ayudas integradas
  const [helps, setHelps] = useState([]);
  const [newHelp, setNewHelp] = useState(emptyHelp);
  const [savingHelp, setSavingHelp] = useState(false);

  const [form, setForm] = useState(emptyForm);

  const accionesPorTipo = {
    grupo: {
      setGroup: setGroupSelected,
      setSubGroups: setSubGroups,
    },
    grupoFiltro: {
      setGroup: setGroupFilterSelected,
      setSubGroups: setSubGroupsFilter,
    },
    grupoSi: {
      setGroup: setGroupSiSelected,
      setSubGroups: setSubGroupsSi,
    },
    grupoNo: {
      setGroup: setGroupNoSelected,
      setSubGroups: setSubGroupsNo,
    },
  };

  const loadSubGroup = async (idgrupo, tipo) => {
    const accion = accionesPorTipo[tipo];
    if (!accion) return;

    try {
      accion.setGroup(idgrupo);

      if (!idgrupo) {
        accion.setSubGroups([]);
        return;
      }

      const res = await apiGetSubGrupos("", idgrupo);
      const subgruposFiltrados = (res.data || []).filter(
        (subgrupo) => String(subgrupo.estado) === "1" || subgrupo.estado === true
      );
      accion.setSubGroups(subgruposFiltrados);
    } catch {
      toast.error("Error cargando subgrupos");
    }
  };

  const loadPreguntaRiesgo = async (idsubgrupo = "") => {
    setLoading(true);
    try {
      const res = await apiGetPreguntas("", idsubgrupo || "");
      setPreguntaRiesgo(res?.data || []);
    } catch {
      toast.error("Error cargando preguntas de riesgo");
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial de todas las preguntas
  useEffect(() => {
    loadPreguntaRiesgo("");
  }, []);

  const loadHelps = async (idPregunta) => {
    if (!idPregunta) {
      setHelps([]);
      return;
    }
    try {
      const res = await apiGetHelpByPregunta(idPregunta);
      const items = res?.data || [];
      setHelps(items);
      setNewHelp({ MasInfo: "", orden: items.length + 1 });
    } catch {
      // Ignorar fallo secundario
    }
  };

  const loadPreguntaRiesgoById = async (idPregunta) => {
    try {
      const res = await apiGetPreguntas(idPregunta, "");

      if (!res?.success || !res.data?.[0]) {
        toast.error("Pregunta no encontrada");
        return;
      }

      const subg = res.data[0];
      setGroupSelected(subg.idGrupo || "");
      setGroupSiSelected(subg.idGrupoSi || "");
      setGroupNoSelected(subg.idGrupoNo || "");

      if (subg.idGrupo) await loadSubGroup(subg.idGrupo, "grupo");
      if (subg.idGrupoSi) await loadSubGroup(subg.idGrupoSi, "grupoSi");
      if (subg.idGrupoNo) await loadSubGroup(subg.idGrupoNo, "grupoNo");

      setForm({
        id: subg.id || "",
        IdSubgrupos: subg.IdSubgrupos || "",
        Orden: subg.Orden ?? "",
        Pregunta: subg.Pregunta || "",
        MasInformacion: subg.MasInformacion || "",
        Soporte: subg.Soporte || "",
        Accion: subg.Accion || "",
        idSubgrupo_Si: subg.idSubgrupo_Si || "",
        idSubgrupo_No: subg.idSubgrupo_No || "",
        Dx: subg.Dx || "",
        sexo: subg.sexo || "A",
      });

      // Cargar ayudas de la pregunta
      await loadHelps(idPregunta);
    } catch {
      toast.error("Error consultando detalle de la pregunta");
    }
  };

  const handleSelectPreguntaRiesgo = (id) => {
    setSelectedPreguntaRiesgo(id);
    loadPreguntaRiesgoById(id);
  };

  const handleNew = () => {
    setSelectedPreguntaRiesgo(null);
    setGroupSelected("");
    setGroupNoSelected("");
    setGroupSiSelected("");
    setForm(emptyForm);
    setHelps([]);
    setNewHelp(emptyHelp);
  };

  const saveQuestions = async () => {
    if (!form.Pregunta?.trim()) {
      toast.warning("El campo Pregunta no puede estar vacío");
      return;
    }
    if (!form.IdSubgrupos) {
      toast.warning("Seleccione el Subgrupo Principal");
      return;
    }
    if (!form.sexo) {
      toast.warning("Seleccione el Sexo aplicable");
      return;
    }
    if (form.Orden === "" || form.Orden === null) {
      toast.warning("El campo Orden es obligatorio");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        Accion: form.Accion || ((form.idSubgrupo_Si || form.idSubgrupo_No) ? "Activar" : ""),
      };
      const res = await apiSavePreguntas(payload);

      if (!res?.success) {
        toast.error(res?.message || "Error al guardar la pregunta");
        return;
      }

      toast.success(res?.message || "Pregunta guardada correctamente");
      await loadPreguntaRiesgo(groupFilterSelected ? "" : "");
      if (form.id) {
        await loadPreguntaRiesgoById(form.id);
      }
    } catch {
      toast.error("Error al procesar el guardado");
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestion = async () => {
    if (!form.id) return;

    try {
      const res = await apiDeletePreguntas(form.id);

      if (!res?.success) {
        toast.error(res?.message || "Error eliminando la pregunta");
        return;
      }

      toast.success(res?.message || "Pregunta eliminada correctamente");
      setPreguntaRiesgo((prev) => prev.filter((item) => String(item.id) !== String(form.id)));
      handleNew();
    } catch {
      toast.error("Error eliminando la pregunta");
    }
  };

  const handleAddHelp = async () => {
    if (!form.id) {
      toast.warning("Primero debe guardar la pregunta para asociarle ayudas");
      return;
    }
    if (!newHelp.MasInfo?.trim()) {
      toast.warning("El texto de la ayuda es obligatorio");
      return;
    }

    setSavingHelp(true);
    try {
      const payload = {
        IdPregunta: form.id,
        MasInfo: newHelp.MasInfo.trim(),
        orden: newHelp.orden || helps.length + 1,
      };
      const res = await apiSaveHelp(payload);
      if (res?.success) {
        toast.success("Ayuda agregada correctamente");
        await loadHelps(form.id);
      } else {
        toast.error(res?.message || "Error al agregar ayuda");
      }
    } catch {
      toast.error("Error interno al agregar ayuda");
    } finally {
      setSavingHelp(false);
    }
  };

  const handleDeleteHelp = async (idHelp) => {
    if (!idHelp) return;
    try {
      const res = await apiDeleteHelp(idHelp);
      if (res?.success) {
        toast.success("Ayuda eliminada");
        setHelps((prev) => prev.filter((h) => (h.Id || h.id) !== idHelp));
      } else {
        toast.error(res?.message || "Error al eliminar la ayuda");
      }
    } catch {
      toast.error("Error eliminando ayuda");
    }
  };

  return {
    subgroups,
    subgroupsFilter,
    subgroupsSi,
    subgroupsNo,
    groupSelected,
    groupFilterSelected,
    groupSiSelected,
    groupNoSelected,
    loadPreguntaRiesgo,
    preguntaRiesgo,
    search,
    setSearch,
    form,
    setForm,
    loading,
    saving,
    savingHelp,
    helps,
    newHelp,
    setNewHelp,
    handleNew,
    loadSubGroup,
    selectedPreguntaRiesgo,
    handleSelectPreguntaRiesgo,
    saveQuestions,
    deleteQuestion,
    handleAddHelp,
    handleDeleteHelp,
  };
};

export default useRiskQuestions;
